import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, RefreshCw, Loader2, Search, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Status = {
  connected: boolean;
  outcome: string;
  latency_ms?: number;
  error?: string | null;
  checked_at: string;
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const SearchConsoleStatus = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);
  const [, tick] = useState(0);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("search-console-status");
      if (!error && data) setStatus(data as Status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [check]);

  const connected = status?.connected;

  return (
    <div className="rounded-lg border border-glass-border bg-charcoal-medium/60 p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className={cn(
          "w-9 h-9 rounded-md flex items-center justify-center shrink-0",
          connected ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
        )}>
          <Search className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="text-foreground">Google Search Console</span>
            {status && (
              <span className={cn(
                "inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded",
                connected ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"
              )}>
                {connected ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {connected ? "Connected" : "Disconnected"}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {status ? (
              <>
                Last verified {formatRelative(status.checked_at)}
                {typeof status.latency_ms === "number" && ` · ${status.latency_ms}ms`}
                {status.outcome && status.outcome !== "verified" && ` · ${status.outcome}`}
                {status.error && ` · ${status.error}`}
              </>
            ) : loading ? "Checking…" : "—"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="border-glass-border text-muted-foreground hover:text-foreground"
        >
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-3.5 h-3.5" />
            <span className="ml-1.5 hidden sm:inline">Open GSC</span>
          </a>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={check}
          disabled={loading}
          className="border-glass-border text-muted-foreground hover:text-foreground"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Re-check</span>
        </Button>
      </div>
    </div>
  );
};
